import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ParticipantResponseDto } from '../dto/response/participant-response.dto';
import participantService from '../services/participant.service';
import { ParticipantSaveRequestDto } from '../dto/request/participant-save-request.dto';
import { ParticipantUpdateRequestDto } from '../dto/request/participant-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';

const initialState = {
  status: 'idle',
  participants: [] as ParticipantResponseDto[] | null,
  participant: {} as ParticipantResponseDto | null,
  error: null as string | null,
};

export const fetchParticipantById = createAsyncThunk(
  'participants/fetchParticipantById',
  async (id: string) => {
    return await participantService.fetchParticipantById(id);
  }
);

export const saveParticipant = createAsyncThunk(
  'participants/saveParticipant',
  async (saveRequest: ParticipantSaveRequestDto) => {
    return await participantService.saveParticipant(saveRequest);
  }
);

export const updateParticipant = createAsyncThunk(
  'participants/updateParticipant',
  async (updateRequest: ParticipantUpdateRequestDto) => {
    return await participantService.updateParticipant(updateRequest);
  }
);

export const deleteParticipant = createAsyncThunk(
  'participants/deleteParticipant',
  async (
    params: { existingId: string; compactId: string },
    { rejectWithValue }
  ) => {
    const { compactId, existingId } = params;
    try {
      return await participantService.deleteParticipant(existingId, compactId);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const participantSlice = createSlice({
  name: 'participantSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchParticipantById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchParticipantById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.participant = action.payload;
      })
      .addCase(fetchParticipantById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveParticipant.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveParticipant.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.participants?.push(action.payload);
        showSuccess('Participant added successfully.');
      })
      .addCase(saveParticipant.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateParticipant.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateParticipant.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Participant saved successfully.');
        if (state.participants) {
          const participantIndex = state.participants.findIndex(
            (participant) => participant.id === action.payload.id
          );
          if (participantIndex !== -1) {
            state.participants[participantIndex] = action.payload;
          }
        }
      })
      .addCase(updateParticipant.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteParticipant.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteParticipant.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Participant deleted successfully.');
      })
      .addCase(deleteParticipant.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting landscape failed. ${action.payload as string}`);
      });
  },
});

export default participantSlice.reducer;
